import React from 'react';
import { useSelector } from 'react-redux';
import PresignedImage from '../common/PresignedImage';
import FileHoverPreview from '../common/FileHoverPreview';
import SharedDragGrip from './SharedDragGrip';
import AttachmentLikeHeart from './AttachmentLikeHeart';
import {
  setSharedAttachmentDragData,
  resolveAttachmentId,
  createAttachmentDragPayload,
} from '../../utils/sharedFilesDrag';
import { useAttachmentTouchInteraction } from '../../utils/sharedFilesTouchDrag';
import { usePrefersCoarsePointer } from '../../utils/usePrefersCoarsePointer';
import { isDisplayableImage } from '../../utils/fileUtils';

const DraggableFileThumb = ({
  file,
  canDrag,
  showGrip = false,
  canRename = false,
  onRename,
  onView,
  onDownload,
  renderIcon,
  onTouchAttachmentDrop,
  onTouchHighlight,
  onSharedDragStart,
  onSharedDragEnd,
  onStandaloneDrop,
  className = '',
}) => {
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const coarsePointer = usePrefersCoarsePointer();
  const isImage = isDisplayableImage(file);
  const handleActivate = onView || onDownload;
  const canDragItem = Boolean(canDrag && file?.messageId && (resolveAttachmentId(file) || file?.fileUrl));
  const payload = createAttachmentDragPayload(file.messageId, file);

  const touchHandlers = useAttachmentTouchInteraction({
    enabled: coarsePointer && canDragItem,
    payload,
    onTap: handleActivate,
    onDrop: onTouchAttachmentDrop,
    onHighlight: onTouchHighlight,
  });

  const handleDragStart = (e) => {
    if (!canDragItem) {
      e.preventDefault();
      return;
    }
    setSharedAttachmentDragData(e, payload);
    window.requestAnimationFrame(() => onSharedDragStart?.());
  };

  const handleDragOver = (e) => {
    if (!canDragItem || !onStandaloneDrop) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    if (!canDragItem || !onStandaloneDrop) return;
    e.preventDefault();
    e.stopPropagation();
    onStandaloneDrop(e, file);
  };

  const gripTitle = canDragItem
    ? (coarsePointer
      ? 'Long-press to drag into a group'
      : 'Drag into a group or drop on another item to create a group')
    : 'Cannot drag';

  const renderGrip = showGrip || canDragItem;

  return (
    <FileHoverPreview
      attachment={file}
      className={`relative flex-shrink-0 ${className}`}
      canRename={canRename}
      onRename={onRename}
    >
      <div
        className={`relative flex items-center ${canDragItem && !coarsePointer ? 'cursor-grab active:cursor-grabbing' : ''}`}
        draggable={canDragItem && !coarsePointer}
        onDragStart={canDragItem && !coarsePointer ? handleDragStart : undefined}
        onDragEnd={canDragItem && !coarsePointer ? () => onSharedDragEnd?.() : undefined}
        onDragOver={canDragItem ? handleDragOver : undefined}
        onDrop={canDragItem ? handleDrop : undefined}
        data-shared-files-standalone-drop={file?.messageId ? resolveAttachmentId(file) : undefined}
        data-standalone-message-id={file?.messageId}
        onDoubleClick={!coarsePointer && onView ? onView : undefined}
        onClick={!coarsePointer && handleActivate ? handleActivate : undefined}
        {...(coarsePointer && canDragItem ? touchHandlers : {})}
      >
        {renderGrip && (
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 z-10 hidden group-hover/file:flex pointer-events-none group-hover/file:pointer-events-auto">
            <SharedDragGrip
              active={canDragItem}
              disabled={!canDragItem}
              title={gripTitle}
              size="md"
            />
          </div>
        )}
        <div className="relative flex-shrink-0">
          {isImage ? (
            <PresignedImage
              fileKey={file.fileUrl}
              originalName={file.fileName}
              className="w-12 h-12 object-cover rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:opacity-85 transition-opacity pointer-events-none select-none"
            />
          ) : (
            <div
              className="w-12 h-12 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent shadow-sm overflow-hidden"
            >
              {renderIcon?.(file) ?? null}
            </div>
          )}
          {isImage && (
            <AttachmentLikeHeart
              attachment={file}
              userId={currentUserId}
              size="sm"
              className="absolute -bottom-0.5 -right-0.5 z-[2] scale-90"
            />
          )}
        </div>
      </div>
    </FileHoverPreview>
  );
};

export default DraggableFileThumb;
